import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { Header } from '@components/Header';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import Desktop from '@components/icons/Desktop.svg';
import Done from '@components/icons/Done.svg';
import DoubleDone from '@components/icons/DoubleDone.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';

import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService, GetOrgMembersMemberResponseDto } from 'app/API';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { useNavigate, useParams } from 'react-router';
import { createFetchOrganizationMembersQueryFn } from '../../api/fetchOrganizationMembers';
import { EntityIconTableMuted30 } from '@components/core-ui/EntityIcon/variations/EntityIconTableMuted30';
import { CellContent } from './styles/CellContent';
import { StyledLink } from './styles/StyledLink';
import { SwitchWrapper } from './styles/SwitchWrapper';
import { Wrapper } from './styles/Wrapper';

export const Content = () => {
  const navigate = useNavigate();
  const { me } = useAuthContext();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { id: routeId } = useParams<{ id: string }>();
  const id = routeId ?? '';

  const membersQuery = useQuery({
    queryKey: organizationsKeys.membersByOrg(id),
    queryFn: createFetchOrganizationMembersQueryFn(call),
    enabled: id !== '',
  });

  const changeEnabledMutation = useMutation({
    mutationFn: ({ uid, disabled }: { uid: string; disabled: boolean }) =>
      call(() => AdminService.setUserDisabled({ uid, requestBody: { disabled } })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: organizationsKeys.membersByOrg(id) }),
  });

  const changeEnabled = (user: GetOrgMembersMemberResponseDto) => {
    changeEnabledMutation.mutate({ uid: user.id, disabled: !user.disabled });
  };

  const users = membersQuery.data?.data ?? [];
  const isLoading = membersQuery.isFetching || changeEnabledMutation.isPending;

  return (
    <>
      <Header
        title="Members"
        backRoute={routes.adminOrganizationsList.make()}
        leftSideContent={[
          <HeaderSwitchWrapper key="tabSwitcher">
            <SwitchButton
              active
              onClick={() => {
                navigate(routes.adminOrganizationUsersList.make(id));
              }}
            >
              Members
            </SwitchButton>
            <SwitchButton
              onClick={() => {
                navigate(routes.adminOrganizationInvitationsList.make(id));
              }}
            >
              Invitations
            </SwitchButton>
          </HeaderSwitchWrapper>,
        ]}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th style={{ width: '1%' }}></Th>
                <Th style={{ width: '20%' }}>Name</Th>
                <Th style={{ width: '50%' }}>Contacts</Th>
                <Th style={{ width: '1%' }}>Created</Th>
                <Th style={{ width: '1%' }}>Enabled</Th>
                <Th style={{ width: '3%', paddingTop: '6px' }} title="Downloaded & Signed in with Desktop App">
                  <Desktop width="16px" />
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((data) => {
                let onChange = () => changeEnabled(data);
                if (me.id === data.id) {
                  onChange = async () => {};
                }

                return (
                  <Tr key={data.id}>
                    <Td>
                      <CellContent>
                        <EntityIconTableMuted30 name={`${data.name} ${data.lastname}`} src={data.avatar ?? undefined} />
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        <StyledLink to={routes.adminUserDetails.make(data.id)}>
                          {data.name} {data.lastname}
                        </StyledLink>
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        <span style={{ display: 'block' }}>{data.email}</span>
                        <span style={{ display: 'block' }}>{data.phone}</span>
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        <SwitchWrapper>
                          <Switch checked={!data.disabled} onChange={onChange} disabled={me.id === data.id} />
                        </SwitchWrapper>
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        {data.signedInWithDesktop ? (
                          <DoubleDone width="24px" />
                        ) : data.downloadedDesktop ? (
                          <Done width="16px" />
                        ) : (
                          ''
                        )}
                      </CellContent>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
      </Wrapper>
    </>
  );
};
